// App.js
import React from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "./context/ThemeContext";
import HomePage from "./HomePage.js";
import Chatbot from "./chatbot.js";
import TechArticlesPage from "./TechArticlesPage.js";
import LoginPage from "./LoginPage.js";
import Pricing from "./pricing.js";
import SuccessPage from "./SuccessPage.js";
import Features from "./Features.js";
import Resources from "./Resources.js";
import Roadmap from "./Roadmap.js";
import Templates from "./Templates.js";
import Impact from "./Impact.js";
import Campaigns from "./Campaigns.js";
import Projects from "./Projects.js";
import Moderation from "./Moderation.js";
import PrivacyPolicy from "./PrivacyPolicy.js";
import DeleteData from "./pages/DeleteData.js";
import Contact from "./Contact.js";
import TermsOfService from "./TermsOfService.js";
import Enterprise from "./Enterprise.js";
import UnifiedAuthPage from "./UnifiedAuthPage.js";
import PrivateRoute from "./PrivateRoute.js";
import SidebarSearch from "./SidebarSearch.js";
import ImageEditor from "./ImageEditor.js";
import VideoEditor from "./VideoEditor.js";
import IDE from "./IDE.js";
import MicrosoftCallback from "./MicrosoftCallback.js";
import AppleCallback from "./AppleCallback.js";
import FacebookCallback from "./FacebookCallback.js";
import Docs from "./Docs.js";
import About from "./About.js";
import Careers from "./Careers.js";
import Overview from "./Overview.js";
import Download from "./Download.js";
import Teams from "./Teams.js";
import Education from "./Education.js";
import Integrations from "./Integrations.js";
import Blog from "./Blog.js";
import BlogArticleAiSoftwareDev from "./BlogArticleAiSoftwareDev.js";
import BlogArticleTeamCollaboration from "./BlogArticleTeamCollaboration.js";
import BlogArticleSecurityAi from "./BlogArticleSecurityAi.js";
import Support from "./Support.js";
import RouteTitle from "./components/RouteTitle.js";

class AppErrorBoundary extends React.Component {
  state = { error: null, info: null };
  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidCatch(error, info) {
    console.error("App error:", error, info?.componentStack);
    this.setState((s) => ({ ...s, info }));
  }
  render() {
    if (this.state.error) {
      return (
        <div role="alert" style={{ padding: 24, fontFamily: "sans-serif", maxWidth: 640 }}>
          <h1>Something went wrong</h1>
          <pre style={{ background: "#f5f5f5", padding: 12, overflow: "auto" }}>
            {this.state.error?.message || String(this.state.error)}
          </pre>
          {this.state.info?.componentStack && (
            <details style={{ marginTop: 12 }}>
              <summary>Component stack</summary>
              <pre style={{ background: "#f5f5f5", padding: 12, fontSize: 12, overflow: "auto" }}>
                {this.state.info.componentStack}
              </pre>
            </details>
          )}
          <button type="button" onClick={() => this.setState({ error: null, info: null })} style={{ marginTop: 12 }}>
            Dismiss
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function App() {
  return (
    <AppErrorBoundary>
      <ThemeProvider>
        <RouteTitle />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/chatbot" element={<Chatbot />} />
          <Route path="/chatbot-page" element={<PrivateRoute><Chatbot /></PrivateRoute>} />
          <Route path="/TechArticlesPage" element={<TechArticlesPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/auth" element={<UnifiedAuthPage />} />
          <Route path="/PrivateRoute" element={<PrivateRoute />} />
          <Route path="/Features" element={<Features />} />
          <Route path="/Enterprise" element={<Enterprise />} />
          <Route path="/SidebarSearch" element={<SidebarSearch />} />
          <Route path="/ImageEditor" element={<ImageEditor />} />
          <Route path="/VideoEditor" element={<VideoEditor />} />
          <Route path="/ide" element={<IDE />} />
          <Route path="/Resources" element={<Resources />} />
          <Route path="/Roadmap" element={<Roadmap />} />
          <Route path="/Templates" element={<Templates />} />
          <Route path="/Impact" element={<Impact />} />
          <Route path="/Campaigns" element={<Campaigns />} />
          <Route path="/Projects" element={<Projects />} />
          <Route path="/Moderation" element={<Moderation />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/events" element={<Navigate to="/" replace />} />
          <Route path="/success" element={<SuccessPage />} />
          <Route path="/PrivacyPolicy" element={<PrivacyPolicy />} />
          <Route path="/TermsOfService" element={<TermsOfService />} />
          <Route path="/delete-data" element={<DeleteData />} />
          <Route path="/Contact" element={<Contact />} />
          <Route path="/docs" element={<Docs />} />
          <Route path="/auth/microsoft/callback" element={<MicrosoftCallback />} />
          <Route path="/auth/apple/callback" element={<AppleCallback />} />
          <Route path="/auth/facebook/callback" element={<FacebookCallback />} />
          <Route path="/About" element={<About />} />
          <Route path="/about" element={<About />} />
          <Route path="/Careers" element={<Careers />} />
          <Route path="/Overview" element={<Overview />} />
          <Route path="/Download" element={<Download />} />
          <Route path="/Teams" element={<Teams />} />
          <Route path="/Education" element={<Education />} />
          <Route path="/Integrations" element={<Integrations />} />
          <Route path="/Apps" element={<Navigate to="/chatbot?view=apps" replace />} />
          <Route path="/Blog" element={<Blog />} />
          <Route path="/blog/ai-transforming-software-development" element={<BlogArticleAiSoftwareDev />} />
          <Route path="/blog/team-collaboration-with-zolaid" element={<BlogArticleTeamCollaboration />} />
          <Route path="/blog/security-best-practices-ai-development" element={<BlogArticleSecurityAi />} />
          <Route path="/Support" element={<Support />} />
        </Routes>
      </ThemeProvider>
    </AppErrorBoundary>
  );
}

export default App;
